import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import Auth from "../auth";

/**
 * Emily502Modal — an easter egg for exactly one office user.
 *
 * A fake 502 Bad Gateway takes over the screen, and the Retry button makes
 * things escalate (502 -> 503 -> 504) before the reveal. Fully closable at
 * every stage (corner X, or "Return to work" at the end), touches nothing
 * real, and disarms itself permanently after EXPIRES_AT.
 *
 * Firing rules: only for TARGET_USER_ID, at most twice per day (localStorage
 * BDR_EMILY_502_KEY tracks {d, n} per day), at a random 2-35 min delay after
 * app load; a second fire the same day only happens with 45% probability.
 *
 * Preview: append ?emily502=preview to any route — shows immediately for
 * anyone, skips identity/timing/counters entirely.
 *
 * Emily: if you are reading this — hi. You found it. Tell Taylor and he owes
 * you lunch.
 */

// Emily Turner (office) — bdr_prod users._id, looked up 2026-07-22.
const TARGET_USER_ID = "656e571148faed2c564645f7";
// Prank self-destructs after two weeks (fires only strictly before this).
const EXPIRES_AT = new Date("2026-08-06T00:00:00");
const BDR_EMILY_502_KEY = "bdr_emily_502";
const MAX_FIRES_PER_DAY = 2;
const SECOND_FIRE_CHANCE = 0.45;

const todayStr = () => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const readMeta = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(BDR_EMILY_502_KEY));
    if (raw && raw.d === todayStr()) return raw;
  } catch (_e) {
    /* fresh start */
  }
  return { d: todayStr(), n: 0 };
};

const writeMeta = (meta) => {
  try {
    localStorage.setItem(BDR_EMILY_502_KEY, JSON.stringify(meta));
  } catch (_e) {
    /* storage unavailable — prank gracefully skipped */
  }
};

// The escalation script. Stage index === number of Retry clicks.
const STAGES = [
  {
    code: "502 Bad Gateway",
    sub: "nginx/1.18.0 (Ubuntu)",
    body: null,
    retryLabel: "Retry",
  },
  {
    code: "503 Service Unavailable",
    sub: "nginx/1.18.0 (Ubuntu)",
    body: "Okay, that made it worse.",
    retryLabel: "Retry harder",
  },
  {
    code: "504 Gateway Timeout",
    sub: "nginx/1.18.0 (Ubuntu)",
    body: "Have you tried turning Emily off and on again?",
    retryLabel: "...retry?",
  },
];

export default function Emily502Modal() {
  const [visible, setVisible] = useState(false);
  const [stage, setStage] = useState(0);

  const isPreview =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("emily502") === "preview";

  useEffect(() => {
    if (isPreview) {
      setVisible(true);
      return undefined;
    }
    if (new Date() >= EXPIRES_AT) return undefined;
    const profile = Auth.loggedIn() ? Auth.getProfile() : null;
    if (profile?.data?._id !== TARGET_USER_ID) return undefined;

    const meta = readMeta();
    if (meta.n >= MAX_FIRES_PER_DAY) return undefined;

    // Random strike 2-35 minutes into the session. Unpredictability is the
    // whole point — never right at login.
    const delayMs = (2 + Math.random() * 33) * 60 * 1000;
    const id = setTimeout(() => {
      const fresh = readMeta();
      if (fresh.n >= MAX_FIRES_PER_DAY) return;
      if (fresh.n === 1 && Math.random() > SECOND_FIRE_CHANCE) return;
      if (new Date() >= EXPIRES_AT) return;
      writeMeta({ d: fresh.d, n: fresh.n + 1 });
      setStage(0);
      setVisible(true);
    }, delayMs);
    return () => clearTimeout(id);
  }, [isPreview]);

  if (!visible) return null;

  const close = () => {
    setVisible(false);
    setStage(0);
  };

  const revealed = stage >= STAGES.length;
  const current = revealed ? null : STAGES[stage];

  const overlay = (
    <div
      style={{
        position: "fixed",
        inset: 0,
        // Highest thing in the app, full stop — above GlobalSnackbar
        // (1100001), every modal, and the deploy banner (1050000).
        zIndex: 1200000,
        background: revealed ? "rgba(10, 8, 26, 0.85)" : "#ffffff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "background 400ms ease",
      }}
    >
      {/* Corner escape hatch — she's never actually stuck. Deliberately
          low-key gray so the fake error page stays convincing. */}
      <button
        onClick={close}
        aria-label="Close"
        style={{
          position: "absolute",
          top: 14,
          right: 18,
          background: "none",
          border: "none",
          fontSize: 22,
          color: revealed ? "#ffffff88" : "#00000033",
          cursor: "pointer",
          lineHeight: 1,
        }}
      >
        {"×"}
      </button>

      {!revealed ? (
        // ---- Fake nginx error page (escalating) ----
        <div
          style={{
            fontFamily: "Times New Roman, Times, serif",
            color: "#000",
            textAlign: "center",
          }}
        >
          <h1 style={{ fontSize: 34, fontWeight: 700, margin: 0 }}>
            {current.code}
          </h1>
          <hr style={{ margin: "12px 0", border: "none", borderTop: "1px solid #000" }} />
          <p style={{ margin: 0, fontSize: 16 }}>{current.sub}</p>
          {current.body && (
            <p style={{ marginTop: 18, fontSize: 17, fontStyle: "italic" }}>
              {current.body}
            </p>
          )}
          <button
            onClick={() => setStage((s) => s + 1)}
            style={{
              marginTop: 26,
              fontFamily: "inherit",
              fontSize: 15,
              padding: "6px 22px",
              background: "#f2f2f2",
              border: "1px solid #999",
              borderRadius: 3,
              cursor: "pointer",
            }}
          >
            {current.retryLabel}
          </button>
        </div>
      ) : (
        // ---- The reveal — house style ----
        <div
          className="bdr-grad-container-med bdr-border-std bdr-shadow-depth"
          style={{
            maxWidth: 460,
            width: "90%",
            padding: "30px 34px",
            color: "#fff",
            textAlign: "center",
          }}
        >
          <h2 style={{ margin: 0, fontSize: 24 }}>Just kidding.</h2>
          <p style={{ fontSize: 16, lineHeight: 1.6, marginTop: 16 }}>
            The server is fine. It has never been better, actually.
            <br />
            The real question is: are <b>you</b> fine, Emily?
          </p>
          <p style={{ fontSize: 13, opacity: 0.75, marginTop: 14 }}>
            Sincerely,
            <br />
            Management (it was Taylor)
          </p>
          <button
            onClick={close}
            style={{
              marginTop: 20,
              background: "#fff",
              color: "#1a3a75",
              border: "none",
              padding: "8px 22px",
              borderRadius: 5,
              fontWeight: 700,
              fontSize: 14,
              cursor: "pointer",
            }}
          >
            Return to work
          </button>
        </div>
      )}
    </div>
  );

  return ReactDOM.createPortal(overlay, document.body);
}
